import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, Plus, Trash2, Upload, ExternalLink, Home } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const emptyItem = { title: "", description: "", icon: "", image_url: "" };
const emptySection = { heading: "", subheading: "", items: [] };
const empty = {
  hero: { title: "", subtitle: "", background_url: "", cta_text: "Iniciar sesión", cta_url: "/login" },
  what_is: { ...emptySection },
  modules: { ...emptySection, items: [] },
  how_it_works: { ...emptySection, items: [] },
  testimonials: { ...emptySection, items: [] },
  cta_secondary: { title: "", subtitle: "", cta_text: "Iniciar sesión", cta_url: "/login" },
  footer: { tagline: "", contact_email: "", contact_phone: "", social_links: {} },
};

const SUPER_MASTER_EMAIL = "fabiolicep@gmail.com";

function SectionEditor({ label, value, onChange, testidPrefix, allowItems = true }) {
  const items = value?.items || [];
  const upd = (patch) => onChange({ ...value, ...patch });
  const updItem = (i, patch) => {
    const next = items.map((it, idx) => idx === i ? { ...it, ...patch } : it);
    upd({ items: next });
  };
  const addItem = () => upd({ items: [...items, { ...emptyItem }] });
  const rmItem = (i) => upd({ items: items.filter((_, idx) => idx !== i) });

  return (
    <div className="card-elevated p-4 sm:p-5 space-y-3" data-testid={`${testidPrefix}-section`}>
      <h3 className="font-serif-display text-lg text-brand-ink font-semibold">{label}</h3>
      <div className="space-y-2">
        <div><Label>Título</Label><Input value={value?.heading || ""} onChange={(e) => upd({ heading: e.target.value })} data-testid={`${testidPrefix}-heading`} /></div>
        <div><Label>Subtítulo</Label><Textarea rows={2} value={value?.subheading || ""} onChange={(e) => upd({ subheading: e.target.value })} data-testid={`${testidPrefix}-subheading`} /></div>
      </div>
      {allowItems && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Items ({items.length})</Label>
            <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid={`${testidPrefix}-add-item`}>
              <Plus className="h-3 w-3 mr-1" /> Añadir
            </Button>
          </div>
          {items.map((it, i) => (
            <div key={i} className="rounded border border-stone-200 p-3 space-y-2 bg-stone-50" data-testid={`${testidPrefix}-item-${i}`}>
              <div className="flex gap-2">
                <Input placeholder="Título" value={it.title || ""} onChange={(e) => updItem(i, { title: e.target.value })} />
                <Button size="sm" variant="ghost" onClick={() => rmItem(i)} className="text-destructive" data-testid={`${testidPrefix}-remove-${i}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              <Textarea rows={2} placeholder="Descripción" value={it.description || ""} onChange={(e) => updItem(i, { description: e.target.value })} />
              <div className="flex gap-2">
                <Input placeholder="Icono (sparkles, instagram, mail, users, rocket, message, youtube…)" value={it.icon || ""} onChange={(e) => updItem(i, { icon: e.target.value })} />
                <Input placeholder="URL de imagen (opcional)" value={it.image_url || ""} onChange={(e) => updItem(i, { image_url: e.target.value })} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HeroEditor({ label, value, onChange, testidPrefix, showBg = true }) {
  const upd = (patch) => onChange({ ...value, ...patch });
  const [uploading, setUploading] = useState(false);
  const uploadBg = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/photo?module=landing_hero", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      upd({ background_url: r.data.url });
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setUploading(false); }
  };
  return (
    <div className="card-elevated p-4 sm:p-5 space-y-3" data-testid={`${testidPrefix}-section`}>
      <h3 className="font-serif-display text-lg text-brand-ink font-semibold">{label}</h3>
      <div><Label>Título</Label><Input value={value?.title || ""} onChange={(e) => upd({ title: e.target.value })} data-testid={`${testidPrefix}-title`} /></div>
      <div><Label>Subtítulo</Label><Textarea rows={3} value={value?.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })} data-testid={`${testidPrefix}-subtitle`} /></div>
      {showBg && (
        <div>
          <Label>Imagen de fondo</Label>
          <div className="flex flex-col sm:flex-row gap-2 items-start">
            <Input value={value?.background_url || ""} onChange={(e) => upd({ background_url: e.target.value })} placeholder="URL o subir" data-testid={`${testidPrefix}-bg-url`} />
            <label className="inline-flex items-center gap-1 rounded-md border border-input bg-white px-3 py-2 text-sm cursor-pointer hover:bg-stone-50">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBg(e.target.files?.[0])} data-testid={`${testidPrefix}-bg-file`} />
              Subir
            </label>
          </div>
          {value?.background_url && <img src={value.background_url} alt="" className="mt-2 h-24 w-40 object-cover rounded border" />}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div><Label>Texto del botón</Label><Input value={value?.cta_text || ""} onChange={(e) => upd({ cta_text: e.target.value })} data-testid={`${testidPrefix}-cta-text`} /></div>
        <div><Label>URL del botón</Label><Input value={value?.cta_url || ""} onChange={(e) => upd({ cta_url: e.target.value })} data-testid={`${testidPrefix}-cta-url`} /></div>
      </div>
    </div>
  );
}

export default function LandingEditor() {
  const { user } = useAuth();
  const isSuper = (user?.email || "").toLowerCase() === SUPER_MASTER_EMAIL;
  const [config, setConfig] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      try {
        const r = await api.get("/landing");
        setConfig({ ...empty, ...r.data });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
      } finally { setLoading(false); }
    })();
  }, [isSuper]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/landing", config);
      toast.success("Landing actualizada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  if (!isSuper) {
    return (
      <div className="card-elevated p-8 text-center">
        <h1 className="font-serif-display text-2xl text-brand-ink font-semibold mb-2">Editor de Landing</h1>
        <p className="text-sm text-stone-500">Sólo el super master puede editar el contenido público.</p>
      </div>
    );
  }
  if (loading) {
    return <div className="card-elevated p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in-up max-w-4xl" data-testid="landing-editor">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <Home className="h-6 w-6 text-brand-emerald" /> Editor de Landing
          </h1>
          <p className="text-sm text-stone-500 mt-2">Los cambios se reflejan de inmediato en la landing pública (raíz `/`).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="landing-preview-link">
            <Link to="/landing" target="_blank"><ExternalLink className="h-4 w-4 mr-1" /> Previsualizar</Link>
          </Button>
          <Button onClick={save} disabled={saving} data-testid="landing-save" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" /> Guardar cambios
          </Button>
        </div>
      </div>

      <HeroEditor label="Hero (encabezado)" value={config.hero} onChange={(v) => setConfig({ ...config, hero: v })} testidPrefix="hero" />
      <SectionEditor label="Sección: Qué es" value={config.what_is} onChange={(v) => setConfig({ ...config, what_is: v })} testidPrefix="whatis" allowItems={false} />
      <SectionEditor label="Sección: Módulos" value={config.modules} onChange={(v) => setConfig({ ...config, modules: v })} testidPrefix="modules" />
      <SectionEditor label="Sección: Cómo funciona" value={config.how_it_works} onChange={(v) => setConfig({ ...config, how_it_works: v })} testidPrefix="how" />
      <SectionEditor label="Sección: Testimonios (opcional)" value={config.testimonials} onChange={(v) => setConfig({ ...config, testimonials: v })} testidPrefix="testimonials" />
      <HeroEditor label="CTA secundario" value={config.cta_secondary} onChange={(v) => setConfig({ ...config, cta_secondary: v })} testidPrefix="cta2" showBg={false} />

      <div className="card-elevated p-4 sm:p-5 space-y-3" data-testid="footer-section">
        <h3 className="font-serif-display text-lg text-brand-ink font-semibold">Footer / Contacto</h3>
        <div><Label>Tagline</Label><Input value={config.footer?.tagline || ""} onChange={(e) => setConfig({ ...config, footer: { ...config.footer, tagline: e.target.value } })} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div><Label>Email de contacto</Label><Input value={config.footer?.contact_email || ""} onChange={(e) => setConfig({ ...config, footer: { ...config.footer, contact_email: e.target.value } })} data-testid="footer-email" /></div>
          <div><Label>Teléfono</Label><Input value={config.footer?.contact_phone || ""} onChange={(e) => setConfig({ ...config, footer: { ...config.footer, contact_phone: e.target.value } })} /></div>
        </div>
      </div>
    </div>
  );
}
